import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Header.css";
import logo from "../../static/logo/logo.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBars,
  faXmark,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { FaHome, FaNewspaper, FaTicketAlt, FaUserShield, FaPenNib, FaSignInAlt } from "react-icons/fa";
import axios from "axios";

/* ✨ Trial banner */
import PremiumTrialBanner from "../PremiumTrial/PremiumTrialBanner";
import "../PremiumTrial/PremiumTrialBanner.css";

const MOBILE_BREAKPOINT = 1024;

const Header = () => {
  const rotatingWords = useMemo(
    () => [
      "Intelligence",
      "Investigations",
      "News",
      "Research",
      "Opinions",
      "Insights",
      "Analytics",
    ],
    []
  );

  const [currentWord, setCurrentWord] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  const [showSearch, setShowSearch] = useState(false);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profilePicture, setProfilePicture] = useState(
    "https://cdn-icons-png.flaticon.com/512/9887/9887959.png"
  );

  const [hasSupportAccess, setHasSupportAccess] = useState(false);
  const [gatesLoaded, setGatesLoaded] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState("Free");

  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileNewsOpen, setMobileNewsOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Refs + state for the sticky/pinned trial bar
  const navRef = useRef(null);
  const trialRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const [trialH, setTrialH] = useState(0);

  useEffect(() => {
    setIsDarkMode(document.documentElement.classList.contains("dark"));
  }, []);

  const handleThemeToggle = () => {
    const nextIsDark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
    setIsDarkMode(nextIsDark);
  };

  // ----- Auth/Profile fetch -----
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(!!token);

    if (!token) {
      setGatesLoaded(true);
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    const fetchUserData = async () => {
      try {
        const [profileResponse, authorResponse, adminResponse, supportAccessResponse, subResponse] =
          await Promise.all([
            axios.get("/api/user/profile-picture", { headers }),
            axios.get("/api/is-author", { headers }),
            axios.get("/api/is-admin", { headers }),
            axios.get("/api/has-support-access", { headers }),
            axios.get("/api/user-subscription", { headers }),
          ]);

        if (
          profileResponse.status === 401 || profileResponse.status === 403 ||
          authorResponse.status === 401  || authorResponse.status === 403 ||
          adminResponse.status === 401   || adminResponse.status === 403
        ) {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }

        setProfilePicture(
          profileResponse.data.profile_pic ||
            "https://cdn-icons-png.flaticon.com/512/9887/9887959.png"
        );
        setIsAuthor(!!authorResponse.data.is_author);
        setIsAdmin(!!adminResponse.data.is_admin);
        setHasSupportAccess(!!supportAccessResponse.data.hasAccess);
        setSubscriptionPlan(String(subResponse.data?.current_plan || "Free"));
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
      } finally {
        setGatesLoaded(true);
      }
    };

    fetchUserData();
  }, []);

  // ----- Typing effect -----
  useEffect(() => {
    const handleTyping = () => {
      const word = rotatingWords[wordIndex];
      if (isDeleting) {
        setCurrentWord((prev) => prev.substring(0, prev.length - 1));
        setTypingSpeed(50);
      } else {
        setCurrentWord((prev) => word.substring(0, prev.length + 1));
        setTypingSpeed(150);
      }

      if (!isDeleting && currentWord === word) {
        setTimeout(() => setIsDeleting(true), 4000);
      }

      if (isDeleting && currentWord === "") {
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % rotatingWords.length);
      }
    };

    const typingTimer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(typingTimer);
  }, [currentWord, isDeleting, wordIndex, typingSpeed, rotatingWords]);

  // ----- Search (desktop overlay) -----
  const toggleSearchBar = () => {
    setShowSearch((prev) => {
      const next = !prev;
      if (next) setTimeout(() => searchInputRef.current?.focus(), 0);
      return next;
    });

    if (hideTimeout) clearTimeout(hideTimeout);
    if (!showSearch) {
      setHideTimeout(setTimeout(() => setShowSearch(false), 5000));
    }
  };

  const handleInteraction = () => {
    if (hideTimeout) clearTimeout(hideTimeout);
    setHideTimeout(setTimeout(() => setShowSearch(false), 15000));
  };

  const handleSearchSubmit = () => {
    const q = searchText.trim();
    if (!q) return;
    window.location.href = `/search-results?query=${encodeURIComponent(q)}`;
  };

  // ----- Mobile behaviors -----
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setMobileSearchOpen(false);
      }
    };
    if (isMobileMenuOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= MOBILE_BREAKPOINT && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setMobileSearchOpen(false);
        setMobileNewsOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
  }, [isMobileMenuOpen]);

  // Trial bar pin logic: pin when we scroll past the nav (desktop only)
  useEffect(() => {
    const measure = () => {
      setTrialH(trialRef.current?.offsetHeight || 0);

      const navEl = navRef.current;
      if (!navEl) {
        setPinned(false);
        return;
      }
      const navBottom = (navEl.offsetTop || 0) + navEl.offsetHeight;
      const desktop = window.innerWidth > MOBILE_BREAKPOINT;
      setPinned(desktop && window.scrollY >= navBottom);
    };

    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    setTimeout(measure, 0);

    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const handleUpgrade = () => {
    window.location.href = "/pricing";
  };

  const plan = String(subscriptionPlan || "").toLowerCase();

  const showTrialBanner =
    gatesLoaded &&
    isAuthenticated &&
    (plan === "free" || plan === "premium-trial");

  return (
    <>
      <header className="header">
        <div className="social-banner">
          <div className="user-actions">
            <button
              type="button"
              className="theme-toggle-btn-header"
              onClick={handleThemeToggle}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? "☀️" : "🌙"}
            </button>

            {isAuthenticated && isAdmin && (
              <a href="/neuracyb-intel-admin" className="author-registration" title="Admin" aria-label="Admin">
                <FaUserShield className="ua-ico" />
                <span className="ua-label">Admin</span>
              </a>
            )}

            {isAuthenticated && hasSupportAccess && (
              <a href="/admin/support-tickets" className="author-registration" title="Support Tickets" aria-label="Support Tickets">
                <FaTicketAlt className="ua-ico" />
                <span className="ua-label">Support Tickets</span>
              </a>
            )}

            {isAuthenticated && isAuthor ? (
              <a href="/my-articles" className="author-registration" title="My Articles" aria-label="My Articles">
                <FaNewspaper className="ua-ico" />
                <span className="ua-label">My Articles</span>
              </a>
            ) : (
              <a href="/become-author" className="author-registration" title="Become an Author" aria-label="Become an Author">
                <FaPenNib className="ua-ico" />
                <span className="ua-label">Become an Author</span>
              </a>
            )}

            {isAuthenticated ? (
              <a href="/my-profile" className="author-registration" title="My Profile" aria-label="My Profile">
                <span className="ua-label">My Profile</span>
                <span className="avatar-wrap">
                  <img
                    src={profilePicture}
                    alt="Profile"
                    className="profile-picture-avatar"
                  />
                </span>
              </a>
            ) : (
              <a href="/login" className="author-registration" title="Login" aria-label="Login">
                <FaSignInAlt className="ua-ico" />
                <span className="ua-label">Login</span>
              </a>
            )}
          </div>

          <a
            href="https://www.facebook.com/profile.php?id=61554882964648"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FontAwesomeIcon icon={faFacebook} className="social-banner-icon" />
          </a>
          <a
            href="https://www.instagram.com/neuracybsecuritylabs/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FontAwesomeIcon icon={faInstagram} className="social-banner-icon" />
          </a>
          <a
            href="https://x.com/NeuraCybSecLabs"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Twitter"
          >
            <FontAwesomeIcon icon={faXTwitter} className="social-banner-icon" />
          </a>
          <a
            href="https://www.youtube.com/@NeuraCybSecLabs"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
          >
            <FontAwesomeIcon icon={faYoutube} className="social-banner-icon" />
          </a>
        </div>

        <div className="top-banner">
          <button
            className="hamburger-btn"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            type="button"
          >
            <FontAwesomeIcon icon={isMobileMenuOpen ? faXmark : faBars} />
          </button>

          <div className="logo">
            <a href="/" aria-label="NeuraCyb News" className="logo-link">
              <span
                className="logo-desktop"
                aria-hidden="true"
                style={{
                  WebkitMaskImage: `url(${logo})`,
                  maskImage: `url(${logo})`,
                }}
              />
              <img src="/nsl-news.png" alt="" className="logo-mobile" />
              {isAuthenticated && subscriptionPlan === "Premium" && (
                <span className="logo-plan-badge logo-plan-badge--pro" aria-label="Pro subscriber">PRO</span>
              )}
              {isAuthenticated && subscriptionPlan === "Enterprise" && (
                <span className="logo-plan-badge logo-plan-badge--ent" aria-label="Enterprise subscriber">ENTERPRISE</span>
              )}
            </a>
          </div>

          <div className="banner-text">
            <h1>
              <span className="title-fixed">Cybersecurity -&nbsp;</span>
              <span className="dynamic-text">{currentWord}</span>
            </h1>
          </div>
        </div>

        <div className="nav-bar" ref={navRef}>
          <nav className="nav-links" aria-label="Primary">
            <a href="/" aria-label="Home">
              <FaHome />
            </a>

            <div className="dropdown">
              <span className="dropbtn">News</span>
              <div className="mega-menu">
                <div className="menu-card">
                  <a href="/category/cyber-attacks">
                    <strong>Cyber Attacks</strong>
                    <p>Reports and analysis of recent attack campaigns.</p>
                  </a>
                </div>
                <div className="menu-card">
                  <a href="/category/data-breaches">
                    <strong>Data Breaches</strong>
                    <p>Exposures, leaks, and affected organizations.</p>
                  </a>
                </div>
                <div className="menu-card">
                  <a href="/category/threats">
                    <strong>Threats</strong>
                    <p>Malware, ransomware, phishing, and more.</p>
                  </a>
                </div>
                <div className="menu-card">
                  <a href="/category/vulnerabilities">
                    <strong>Vulnerabilities</strong>
                    <p>Flaws in software, CVEs, and patches.</p>
                  </a>
                </div>
                <div className="menu-card">
                  <a href="/category/scams">
                    <strong>Scams</strong>
                    <p>Cyber frauds, social engineering, and tricks.</p>
                  </a>
                </div>
                <div className="menu-card">
                  <a href="/category/research">
                    <strong>Research</strong>
                    <p>Deep dives, whitepapers, and investigations.</p>
                  </a>
                </div>
              </div>
            </div>

            <a href="/cyber-intelligence">Threat Intelligence</a>
            <a href="/investigation-tools">Threat Investigation</a>
            <a href="/cyber-arena">Threat Games</a>
            <a href="/chirp-program">CHIRP</a>
          </nav>

          <div className="nav-actions">
            {!showSearch && (
              <button
                className="search-icon-btn"
                aria-label="Open search"
                onClick={toggleSearchBar}
                type="button"
              >
                <FontAwesomeIcon icon={faSearch} className="search-toggle" />
              </button>
            )}

            <a href="/report" className="report-button">
              Report IoCs
            </a>

            <div
              className={`search-bar-wrapper overlay ${showSearch ? "visible" : ""}`}
              onMouseMove={handleInteraction}
              onTouchStart={handleInteraction}
            >
              <span
                className="close-search"
                role="button"
                tabIndex={0}
                aria-label="Close search"
                onClick={() => setShowSearch(false)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && setShowSearch(false)
                }
              >
                ✕
              </span>
              <input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
                ref={searchInputRef}
              />
              <button
                className="search-button"
                onClick={handleSearchSubmit}
                type="button"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div
            className="mobile-backdrop"
            onClick={() => {
              setIsMobileMenuOpen(false);
              setMobileSearchOpen(false);
            }}
            aria-hidden="true"
          />
        )}

        <div
          id="mobile-menu"
          className={`mobile-drawer ${isMobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
        >
          <div className="mobile-drawer-header">
            <span>Menu</span>
            <button
              type="button"
              className="mobile-close-btn"
              aria-label="Close menu"
              onClick={() => {
                setIsMobileMenuOpen(false);
                setMobileSearchOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="mobile-sheet">
            <nav className="mobile-nav" aria-label="Mobile">
              <a href="/" onClick={() => setIsMobileMenuOpen(false)}>
                <FaHome /> &nbsp;Home
              </a>

              <button
                type="button"
                className={`mobile-accordion ${mobileNewsOpen ? "open" : ""}`}
                aria-expanded={mobileNewsOpen}
                aria-controls="mobile-news-panel"
                onClick={() => setMobileNewsOpen((v) => !v)}
              >
                <span>News</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`chev ${mobileNewsOpen ? "rot" : ""}`}
                />
              </button>

              {mobileNewsOpen && (
                <div id="mobile-news-panel" className="mobile-submenu">
                  <a href="/category/cyber-attacks" onClick={() => setIsMobileMenuOpen(false)}>
                    Cyber Attacks
                  </a>
                  <a href="/category/data-breaches" onClick={() => setIsMobileMenuOpen(false)}>
                    Data Breaches
                  </a>
                  <a href="/category/threats" onClick={() => setIsMobileMenuOpen(false)}>
                    Threats
                  </a>
                  <a href="/category/vulnerabilities" onClick={() => setIsMobileMenuOpen(false)}>
                    Vulnerabilities
                  </a>
                  <a href="/category/scams" onClick={() => setIsMobileMenuOpen(false)}>
                    Scams
                  </a>
                  <a href="/category/research" onClick={() => setIsMobileMenuOpen(false)}>
                    Research
                  </a>
                </div>
              )}

              <a
                href="/cyber-intelligence"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Threat Intelligence
              </a>
              <a
                href="/investigation-tools"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Threat Investigation
              </a>
              <a href="/cyber-arena" onClick={() => setIsMobileMenuOpen(false)}>
                Threat Games
              </a>
              <a href="/chirp-program" onClick={() => setIsMobileMenuOpen(false)}>
                CHIRP
              </a>

              <div className="mobile-actions">
                <a
                  href="/report"
                  className="report-button"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Report
                </a>
                <button
                  type="button"
                  className="mobile-search-trigger"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMobileSearchOpen((v) => !v);
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} /> Search
                </button>
              </div>

              {mobileSearchOpen && (
                <form
                  className="mobile-search-row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!searchText.trim()) return;
                    handleSearchSubmit();
                    setIsMobileMenuOpen(false);
                    setMobileSearchOpen(false);
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    autoFocus
                  />
                  <button type="submit" className="search-button">
                    Go
                  </button>
                  <button
                    type="button"
                    className="mobile-search-cancel"
                    onClick={() => setMobileSearchOpen(false)}
                  >
                    Cancel
                  </button>
                </form>
              )}

              <hr className="mobile-divider" />

              <div className="mobile-footer">
                <div className="mobile-section-label">Follow</div>
                <div className="mobile-footer-icons">
                  <a href="https://www.facebook.com/profile.php?id=61554882964648" aria-label="Facebook">
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                  <a href="https://www.instagram.com/neuracybsecuritylabs/" aria-label="Instagram">
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                  <a href="https://x.com/NeuraCybSecLabs" aria-label="Twitter / X">
                    <FontAwesomeIcon icon={faXTwitter} />
                  </a>
                  <a href="https://www.youtube.com/@NeuraCybSecLabs" aria-label="YouTube">
                    <FontAwesomeIcon icon={faYoutube} />
                  </a>
                </div>
                <div className="mobile-brand-note">NeuraCyb • Intelligence & Insights</div>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {false && showTrialBanner && (
        <>
          <div className="trial-placeholder" style={{ height: pinned ? trialH : 0 }} />
          <div ref={trialRef} className={`trial-below-nav ${pinned ? "is-pinned" : ""}`}>
            <div className="trial-inner">
              <PremiumTrialBanner onUpgrade={handleUpgrade} compact />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;